import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Badge,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  CleaningServices as CleaningIcon,
  Build as MaintenanceIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  Home as SiteIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  PlayArrow as StartIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignIcon,
  Camera as CameraIcon,
  QrCode as QRCodeIcon,
  Dashboard as DashboardIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';

interface HousekeepingTask {
  _id: string;
  taskId: string;
  title: string;
  description: string;
  type: string;
  priority: string;
  category: string;
  site: {
    _id: string;
    siteNumber: string;
    type: string;
  };
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  assignedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  estimatedDuration: number;
  actualDuration?: number;
  scheduledDate: string;
  scheduledTime?: string;
  startedAt?: string;
  completedAt?: string;
  status: string;
  completionNotes?: string;
  qualityCheck?: {
    passed: boolean;
    score: number;
    notes: string;
  };
  supplies: Array<{
    name: string;
    quantity: number;
    unit: string;
    cost: number;
  }>;
  tags: string[];
  createdAt: string;
}

interface SiteStatus {
  _id: string;
  site: {
    _id: string;
    siteNumber: string;
    type: string;
  };
  currentStatus: string;
  housekeeping: {
    status: string;
    lastCleaned?: string;
    lastCleanedBy?: {
      firstName: string;
      lastName: string;
    };
    lastInspected?: string;
    inspectionScore?: number;
  };
  maintenance: {
    status: string;
    lastMaintenance?: string;
    nextMaintenanceDue?: string;
  };
  alerts: Array<{
    _id: string;
    type: string;
    message: string;
    severity: string;
    acknowledged: boolean;
    resolved: boolean;
    createdAt: string;
  }>;
}

interface DashboardData {
  tasksByStatus: Array<{
    _id: string;
    count: number;
    totalEstimatedTime: number;
  }>;
  siteStatuses: Array<{
    _id: string;
    count: number;
  }>;
  overdueTasksCount: number;
  todaysTasksCount: number;
  staffWorkload: Array<{
    _id: string;
    staffName: string;
    taskCount: number;
    totalEstimatedTime: number;
  }>;
  alertsCount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`housekeeping-tabpanel-${index}`}
      aria-labelledby={`housekeeping-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'Urgent': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'info';
    case 'Low': return 'success';
    default: return 'default';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Completed': return 'success';
    case 'In Progress': return 'info';
    case 'Overdue': return 'error';
    case 'Assigned': return 'warning';
    case 'Pending': return 'default';
    default: return 'default';
  }
};

export default function HousekeepingPage() {
  const [tabValue, setTabValue] = useState(0);
  const [tasks, setTasks] = useState<HousekeepingTask[]>([]);
  const [siteStatuses, setSiteStatuses] = useState<SiteStatus[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<HousekeepingTask | null>(null);
  const [createTaskDialog, setCreateTaskDialog] = useState(false);
  const [taskDetailsDialog, setTaskDetailsDialog] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: '',
    assignedTo: ''
  });

  useEffect(() => {
    fetchDashboardData();
    fetchTasks();
    fetchSiteStatuses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/housekeeping/dashboard');
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params: any = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await api.get('/housekeeping/tasks', { params });
      setTasks(response.data.tasks);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteStatuses = async () => {
    try {
      const response = await api.get('/housekeeping/sites/status');
      setSiteStatuses(response.data);
    } catch (err) {
      console.error('Error fetching site statuses:', err);
    }
  };

  const handleStartTask = async (taskId: string) => {
    try {
      await api.post(`/housekeeping/tasks/${taskId}/start`);
      fetchTasks();
      fetchDashboardData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start task');
    }
  };

  const handleCompleteTask = async (taskId: string, notes?: string) => {
    try {
      await api.post(`/housekeeping/tasks/${taskId}/complete`, { notes });
      fetchTasks();
      fetchDashboardData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete task');
    }
  };

  const MetricCard = ({ title, value, subtitle, icon, color = 'primary' }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color?: string;
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
            {subtitle && (
              <Typography color="textSecondary" variant="body2">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Housekeeping Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateTaskDialog(true)}
          >
            Create Task
          </Button>
          <Button
            variant="contained"
            startIcon={<DashboardIcon />}
            onClick={fetchDashboardData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Dashboard" />
          <Tab label="Tasks" />
          <Tab label="Site Status" />
          <Tab label="Analytics" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {dashboardData && (
          <Grid container spacing={3}>
            {/* Key Metrics */}
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Today's Tasks"
                value={dashboardData.todaysTasksCount}
                subtitle="Scheduled for today"
                icon={<TaskIcon fontSize="large" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Overdue Tasks"
                value={dashboardData.overdueTasksCount}
                subtitle="Need immediate attention"
                icon={<WarningIcon fontSize="large" />}
                color="error"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Active Alerts"
                value={dashboardData.alertsCount}
                subtitle="Unresolved alerts"
                icon={<WarningIcon fontSize="large" />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Staff Active"
                value={dashboardData.staffWorkload.length}
                subtitle="With assigned tasks"
                icon={<PersonIcon fontSize="large" />}
              />
            </Grid>

            {/* Task Status Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Task Status Distribution
                </Typography>
                <List>
                  {dashboardData.tasksByStatus.map((status) => (
                    <ListItem key={status._id}>
                      <ListItemIcon>
                        <Chip
                          label={status.count}
                          color={getStatusColor(status._id) as any}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={status._id}
                        secondary={`${Math.round(status.totalEstimatedTime / 60)} hours estimated`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Site Status Summary */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Site Status Summary
                </Typography>
                <List>
                  {dashboardData.siteStatuses.map((status) => (
                    <ListItem key={status._id}>
                      <ListItemIcon>
                        <Badge badgeContent={status.count} color="primary">
                          <SiteIcon />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary={status._id} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Staff Workload */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Staff Workload
                </Typography>
                <Grid container spacing={2}>
                  {dashboardData.staffWorkload.map((staff) => (
                    <Grid item xs={12} sm={6} md={4} key={staff._id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1">
                                {staff.staffName}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {staff.taskCount} tasks ({Math.round(staff.totalEstimatedTime / 60)}h)
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Assigned">Assigned</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Cleaning">Cleaning</MenuItem>
                  <MenuItem value="Maintenance">Maintenance</MenuItem>
                  <MenuItem value="Inspection">Inspection</MenuItem>
                  <MenuItem value="Setup">Setup</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  label="Priority"
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={fetchTasks}
                fullWidth
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={3}>
          {tasks.map((task) => (
            <Grid item xs={12} md={6} lg={4} key={task._id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h3">
                      {task.title}
                    </Typography>
                    <Chip
                      label={task.priority}
                      color={getPriorityColor(task.priority) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography color="textSecondary" gutterBottom>
                    {task.description}
                  </Typography>
                  
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={task.type} size="small" variant="outlined" />
                    <Chip
                      label={task.status}
                      color={getStatusColor(task.status) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary">
                    Site: {task.site?.siteNumber || 'N/A'} ({task.site?.type || 'Unknown'})
                  </Typography>
                  
                  {task.assignedTo && (
                    <Typography variant="body2" color="textSecondary">
                      Assigned to: {task.assignedTo.firstName} {task.assignedTo.lastName}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="textSecondary">
                    Scheduled: {new Date(task.scheduledDate).toLocaleDateString()}
                    {task.scheduledTime && ` at ${task.scheduledTime}`}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary">
                    Duration: {task.estimatedDuration} minutes
                    {task.actualDuration && ` (actual: ${task.actualDuration})`}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedTask(task);
                        setTaskDetailsDialog(true);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  
                  {task.status === 'Assigned' && (
                    <Tooltip title="Start Task">
                      <IconButton 
                        size="small" 
                        onClick={() => handleStartTask(task._id)}
                        color="primary"
                      >
                        <StartIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {task.status === 'In Progress' && (
                    <Tooltip title="Complete Task">
                      <IconButton 
                        size="small" 
                        onClick={() => handleCompleteTask(task._id)}
                        color="success"
                      >
                        <CompleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Edit Task">
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {siteStatuses.map((siteStatus) => (
            <Grid item xs={12} sm={6} md={4} key={siteStatus._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Site {siteStatus.site?.siteNumber || 'N/A'}
                  </Typography>
                  
                  <Chip
                    label={siteStatus.currentStatus}
                    color={siteStatus.currentStatus === 'Available' ? 'success' : 
                           siteStatus.currentStatus === 'Occupied' ? 'info' :
                           siteStatus.currentStatus === 'Maintenance' ? 'warning' : 'default'}
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Type: {siteStatus.site.type}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Housekeeping: {siteStatus.housekeeping.status}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Maintenance: {siteStatus.maintenance.status}
                  </Typography>
                  
                  {siteStatus.alerts.filter(alert => !alert.resolved).length > 0 && (
                    <Chip
                      label={`${siteStatus.alerts.filter(alert => !alert.resolved).length} Alert(s)`}
                      color="warning"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>
          Performance Analytics
        </Typography>
        <Typography color="textSecondary">
          Advanced analytics and reporting features will be available here.
        </Typography>
      </TabPanel>

      {/* Task Details Dialog */}
      <Dialog 
        open={taskDetailsDialog} 
        onClose={() => setTaskDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Task Details: {selectedTask?.title}
        </DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Description:</strong> {selectedTask.description}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {selectedTask.type} | <strong>Priority:</strong> {selectedTask.priority}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Site:</strong> {selectedTask.site?.siteNumber || 'N/A'} ({selectedTask.site?.type || 'Unknown'})
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Status:</strong> {selectedTask.status}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Scheduled:</strong> {new Date(selectedTask.scheduledDate).toLocaleString()}
              </Typography>
              {selectedTask.assignedTo && (
                <Typography variant="body1" gutterBottom>
                  <strong>Assigned to:</strong> {selectedTask.assignedTo.firstName} {selectedTask.assignedTo.lastName}
                </Typography>
              )}
              {selectedTask.completionNotes && (
                <Typography variant="body1" gutterBottom>
                  <strong>Completion Notes:</strong> {selectedTask.completionNotes}
                </Typography>
              )}
              {selectedTask.supplies.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Supplies:</strong>
                  </Typography>
                  <List dense>
                    {selectedTask.supplies.map((supply, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`${supply.name} - ${supply.quantity} ${supply.unit}`}
                          secondary={supply.cost ? `Cost: $${supply.cost}` : ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog 
        open={createTaskDialog} 
        onClose={() => setCreateTaskDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary" gutterBottom>
            Task creation form will be implemented here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTaskDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}